// src/hooks/usePersonalizationSummary.js
import { useState, useEffect, useMemo } from 'react';
import { useClientProfile } from './useClientProfile';

/**
 * Hook to fetch and compute personalization summary data
 * Aggregates profile, survey, and document completion metrics
 *
 * @param {string} [clientId] - Optional client ID override (defaults to session.client_id)
 * @returns {{
 *   summary: Object|null,
 *   isLoading: boolean,
 *   error: Error|null
 * }}
 */
export function usePersonalizationSummary(clientId = null) {
  const { data: profile, isLoading: profileLoading, error: profileError } = useClientProfile(clientId);
  
  // TODO: Replace with real survey hook when available
  // For now, using placeholder values that can be swapped out
  const [surveyData, setSurveyData] = useState({
    strategy: { answered: 10, total: 10, percent: 100, lastUpdated: null },
    capabilities: { answered: 13, total: 20, percent: 65, lastUpdated: null },
    readiness: { answered: 8, total: 20, percent: 40, lastUpdated: null }
  });
  
  // TODO: Replace with real documents hook when available
  // For now, using placeholder values
  const [documentsData, setDocumentsData] = useState({
    count: 0,
    processedCount: 0
  });

  // Calculate profile completion percentage based on required fields
  const profileCompletionPercent = useMemo(() => {
    if (!profile) return 0;
    
    // Define required fields for profile completion
    const requiredFields = [
      'organization_name',
      'organization_type',
      'beds',
      'annual_revenue',
      'annual_admissions',
      'provider_count',
      'ehr_vendor',
      'location_type'
    ];
    
    const filledFields = requiredFields.filter(field => {
      const value = profile[field];
      return value !== null && value !== undefined && value !== '';
    });
    
    return Math.round((filledFields.length / requiredFields.length) * 100);
  }, [profile]);

  // Calculate overall completion percentage
  const overallPercent = useMemo(() => {
    // Weight each component equally (profile + 3 surveys + documents = 5 components)
    const components = [
      profileCompletionPercent,
      surveyData.strategy.percent,
      surveyData.capabilities.percent,
      surveyData.readiness.percent,
      documentsData.count > 0 ? 100 : 0  // Documents are binary: uploaded or not
    ];
    
    const sum = components.reduce((acc, val) => acc + val, 0);
    return Math.round(sum / components.length);
  }, [profileCompletionPercent, surveyData, documentsData]);

  // Assemble the summary object
  const summary = useMemo(() => {
    if (!profile && profileLoading) return null;
    
    return {
      profile: profile || null,
      profileCompletionPercent,
      surveys: {
        strategy: surveyData.strategy,
        capabilities: surveyData.capabilities,
        readiness: surveyData.readiness
      },
      documents: documentsData,
      overallPercent
    };
  }, [profile, profileLoading, profileCompletionPercent, surveyData, documentsData, overallPercent]);

  // Aggregate loading state
  const isLoading = profileLoading;
  
  // Aggregate error state
  const error = profileError;

  return {
    summary,
    isLoading,
    error
  };
}